import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaFileDownload, FaArrowLeft, FaEye, FaSpinner, FaFile
} from "react-icons/fa";

const RegisteredAgentPreview = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchDocumentDetails();
  }, [documentId]);

  const fetchDocumentDetails = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("Authentication required");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/user`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch document details");

      const data = await response.json();
      const documents = data.user.registeredAgentDocuments || [];
      const selectedDocument = documents.find(doc => doc._id === documentId || doc.id === documentId);

      if (!selectedDocument) {
        setError("Document not found");
      } else {
        setDocument(selectedDocument);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (document) {
      const fullUrl = `${process.env.REACT_APP_API_BASE_URL}${document.fileUrl}`;
      window.open(fullUrl, '_blank');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FaSpinner className="w-12 h-12 text-primary-500 animate-spin mx-auto" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="p-6 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 inline-block">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/registered-agent')}
            className="px-4 py-2 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="p-8 text-center">
        <div className="p-6 rounded-xl bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/30 inline-block">
          <p className="text-yellow-600 dark:text-yellow-400 mb-4">Document not found</p>
          <button
            onClick={() => navigate('/registered-agent')}
            className="px-4 py-2 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/registered-agent')}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
          >
            <FaArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-dark-800 dark:text-white">Document Preview</h1>
            <p className="text-gray-500 dark:text-gray-400">Preview and download your registered agent document</p>
          </div>
        </div>
        <button
          onClick={handleDownload}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-500 text-white hover:bg-primary-600 transition-colors"
        >
          <FaFileDownload className="w-4 h-4" />
          Download
        </button>
      </div>

      {/* Document Information */}
      <div className="card">
        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center gap-2">
          <FaFile className="w-5 h-5 text-primary-500" />
          <h2 className="font-semibold text-dark-800 dark:text-white">Document Information</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Document Name</p>
              <p className="font-medium text-dark-800 dark:text-white">{document.originalName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">File Size</p>
              <p className="font-medium text-dark-800 dark:text-white">{formatFileSize(document.size || 0)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Upload Date</p>
              <p className="font-medium text-dark-800 dark:text-white">{new Date(document.date).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">File Type</p>
              <p className="font-medium text-dark-800 dark:text-white">
                {document.fileUrl ? document.fileUrl.split('.').pop().toUpperCase() : 'Unknown'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview */}
      <div className="card overflow-hidden">
        <div className="p-4 border-b border-gray-100 dark:border-dark-700 flex items-center gap-2">
          <FaEye className="w-5 h-5 text-primary-500" />
          <h2 className="font-semibold text-dark-800 dark:text-white">Document Preview</h2>
        </div>
        <div className="bg-gray-100 dark:bg-dark-700">
          <iframe
            src={`${process.env.REACT_APP_API_BASE_URL}${document.fileUrl}`}
            className="w-full h-[600px] border-0"
            title={`Preview of ${document.originalName}`}
          />
        </div>
      </div>
    </div>
  );
};

export default RegisteredAgentPreview;
